import React, { useCallback, useEffect, useState } from 'react';
import PageHeaderText from 'components/base/page-header-text';
import { ENUM_REQUEST_STATUS } from 'models/common.model';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import { ToastMessage } from '@cokitchen/cokitchen-components';
import ActivityIndicator from 'components/base/loader/activityIndicator';
import RequestListFilters from 'components/base/request-list-filters';
import { debounce } from 'utils/functions';
import useDashboardContext from 'contexts/dashboard.context';
import RequestCard from 'components/kitchen-request-card';

export type ExtendedRequestType = ENUM_REQUEST_STATUS | '';

type RequestStatusFilter = ENUM_REQUEST_STATUS | '';

export type FilterType = {
  search: string;
  status: RequestStatusFilter[];
  dateRange: { startDate: Date | null; endDate: Date | null };
  current_page: number;
};

export const KitchenItemRequests = () => {
  const [filters, setFilters] = useState<FilterType>({
    search: '',
    status: [],
    dateRange: {
      startDate: null,
      endDate: null
    },
    current_page: 1
  });
  const [isFetchingItemsRequest, setIsFetchingItemsRequest] = useState(false);
  const [requests, setRequests] = useState<[]>([]);
  const [lastPage, setLastPage] = useState(1);
  const { activeStore } = useDashboardContext();

  const fetchItemsRequest = async (
    searchString: string,
    statusValues: RequestStatusFilter[],
    current_page?: number,
    startDate?: Date | null,
    endDate?: Date | null
  ) => {
    if (!activeStore?.id) return;
    try {
      setIsFetchingItemsRequest(true);

      const normalizedStatus = statusValues;
      const statusParam =
        normalizedStatus.length === 0
          ? undefined
          : normalizedStatus.length === 1
          ? normalizedStatus[0]
          : normalizedStatus;

      const params: Record<string, any> = {
        item_type: 'meal',
        code: searchString,
        status: statusParam,
        page: current_page || '1'
      };

      if (startDate) {
        params.start_date = startDate;
      }
      if (endDate) {
        params.end_date = endDate;
      }

      const response = await apiService.get(`prep-kitchen-request/stores/${activeStore.id}`, {
        version: 'v2',
        params
      });
      if (response) {
        setRequests(response?.data?.data);
        setLastPage(Math.ceil(response?.data?.pagination / 10));
      }
    } catch (err: any) {
      toast(
        <ToastMessage
          message={err.response?.data?.message || 'Error fetching kitchen requests'}
          type='error'
        />
      );
    } finally {
      setIsFetchingItemsRequest(false);
    }
  };
  const debounceFetchItemsRequest = useCallback(debounce(fetchItemsRequest, 1000), []);

  useEffect(() => {
    setIsFetchingItemsRequest(true);
    debounceFetchItemsRequest(
      filters.search,
      filters.status,
      filters.current_page,
      filters.dateRange.startDate,
      filters.dateRange.endDate
    );
  }, [filters, activeStore?.id]);

  return (
    <div>
      <PageHeaderText
        title='Manage Requests (Production meals)'
        description='Manage kitchen item requests'
      />
      <RequestListFilters
        statusOptions={[
          { text: ENUM_REQUEST_STATUS.REQUESTED, value: ENUM_REQUEST_STATUS.REQUESTED },
          { text: ENUM_REQUEST_STATUS.CANCELLED, value: ENUM_REQUEST_STATUS.CANCELLED },
          { text: ENUM_REQUEST_STATUS.SENT, value: ENUM_REQUEST_STATUS.SENT },
          { text: ENUM_REQUEST_STATUS.RECEIVED, value: ENUM_REQUEST_STATUS.RECEIVED },
          { text: ENUM_REQUEST_STATUS.REVIEWED, value: ENUM_REQUEST_STATUS.REVIEWED }
        ]}
        filters={filters}
        onFilterChange={setFilters}
        pageCount={lastPage}
      />
      {isFetchingItemsRequest ? (
        <div className='mt-10'>
          <ActivityIndicator />
        </div>
      ) : requests.length === 0 ? (
        <div className='flex w-full justify-center py-5'>
          <p className='text-gray-400'>No request found</p>
        </div>
      ) : (
        <div className='mt-10 gap-4 grid grid-cols-3 max-[1250px]:grid-cols-2 max-[768px]:grid-cols-1'>
          {requests?.map((p, i) => (
            <RequestCard key={i} request={p} />
          ))}
        </div>
      )}
    </div>
  );
};

export default KitchenItemRequests;
